import React, { useEffect, useRef, useState } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import {
  Calendar,
  Clock,
  MapPin,
  Heart,
  Sparkles,
  Send,
  CheckCircle2,
  Navigation,
  Music,
  Sun,
  Flame,
  X,
  User,
  Images,
} from 'lucide-react';
import type { InvitationData, TemplateMetadata } from '../types';
import { registry } from '../registry';
import './theme.css';

// Register GSAP ScrollTrigger plugin safely
gsap.registerPlugin(ScrollTrigger);

const ASSET_BASE = '/assets/templates/video-03-radha-krishna';

const META: TemplateMetadata = {
  id: 'exp-gsap-radha-krishna',
  name: 'Radha Krishna GSAP Scroll Divine',
  occasionType: 'WEDDING',
  description:
    'An opulent GSAP ScrollTrigger scroll-driven traditional Radha Krishna wedding invitation featuring 3D perspective animations, floating divine lotus and peacock feather parallax, pinned ritual scenes, and single scroll ergonomics.',
  isPremium: true,
  tags: [
    'radha-krishna',
    'gsap',
    'scroll-driven',
    'traditional',
    'wedding',
    'hindu-gods',
    'divine',
    'cinematic',
    '3d',
    'scroll-experience',
    'ai-native',
    'krishna',
    'radha',
    'mandap',
    'haldi',
    'phere',
  ],
  colorScheme: { primary: '#E5C158', accent: '#8A0F3C', bg: '#0D0614' },
};

interface RitualEvent {
  title: string;
  devotionalName: string;
  icon: React.ReactNode;
  date: string;
  time: string;
  venue: string;
  description: string;
  bgImage: string;
}

export function GSAPRadhaKrishnaTemplate({ data }: { data: InvitationData }) {
  const containerRef = useRef<HTMLDivElement>(null);
  const heroRef = useRef<HTMLDivElement>(null);
  const heroBgRef = useRef<HTMLDivElement>(null);

  const ganeshaRef = useRef<HTMLDivElement>(null);
  const ganeshaBgRef = useRef<HTMLDivElement>(null);

  const vrindavanRef = useRef<HTMLDivElement>(null);
  const vrindavanBgRef = useRef<HTMLDivElement>(null);

  const ritualContainerRef = useRef<HTMLDivElement>(null);
  const ritualBgRef = useRef<HTMLDivElement>(null);

  const venueRef = useRef<HTMLDivElement>(null);
  const venueBgRef = useRef<HTMLDivElement>(null);

  const coupleRef = useRef<HTMLDivElement>(null);
  const galleryRef = useRef<HTMLDivElement>(null);

  // Type-safe Data Extraction with Fallbacks
  const rawData = data as unknown as Record<string, any>;
  const customData = (data.customData || {}) as Record<string, any>;
  const weddingData = (customData.wedding || {}) as Record<string, any>;
  const expMeta = (customData.meta || customData.experienceMeta || {}) as Record<string, any>;

  const groom =
    weddingData.groom?.fullName ||
    weddingData.groom?.nickname ||
    expMeta.groomName ||
    expMeta.secondaryName ||
    rawData.groomName ||
    (data.eventName ? data.eventName.split(/ (?:&|and) /i)[0]?.trim() : '') ||
    'Radhe';

  const bride =
    weddingData.bride?.fullName ||
    weddingData.bride?.nickname ||
    expMeta.brideName ||
    expMeta.primaryName ||
    rawData.brideName ||
    (data.eventName ? data.eventName.split(/ (?:&|and) /i)[1]?.trim() : '') ||
    'Krishna';

  const dateStr = rawData._dateInput || data.eventDate || expMeta.weddingDate || expMeta.eventDate || 'Saturday, 12 December 2026';
  const timeStr = data.eventTime || expMeta.eventTime || '10:30 AM IST';
  const venueStr = data.eventVenue || expMeta.venueName || weddingData.venueName || 'Grand Vrindavan Palace';
  const addressStr =
    weddingData.venueAddress || expMeta.venueAddress || data.eventVenue || 'Chhatikara Road, Vrindavan, Uttar Pradesh';
  const cityStr = expMeta.city || 'Vrindavan';
  const mapLink =
    data.rsvpLink || `https://maps.google.com/?q=${encodeURIComponent(venueStr + ' ' + addressStr)}`;
  const message =
    data.customMessage ||
    expMeta.tagline ||
    expMeta.customMessage ||
    'Together with their families, invite you to share in the divine joy of their wedding celebration as two souls unite in timeless love.';
  const host = data.hostName || expMeta.hostedBy || expMeta.hostName || 'Sharma & Verma Families';

  // Uploaded couple photos & gallery — real guest-facing content, not mock/AI artwork
  const groomPhoto: string | undefined =
    expMeta.referenceImages?.groom ||
    expMeta.groomPhotoUrl ||
    expMeta.groomPhoto ||
    customData.groomPhotoUrl ||
    customData.groomPhoto ||
    weddingData.groom?.photoUrl;

  const bridePhoto: string | undefined =
    expMeta.referenceImages?.bride ||
    expMeta.bridePhotoUrl ||
    expMeta.bridePhoto ||
    customData.bridePhotoUrl ||
    customData.bridePhoto ||
    weddingData.bride?.photoUrl;

  const couplePhoto: string | undefined =
    expMeta.referenceImages?.couple ||
    expMeta.couplePhotoUrl ||
    expMeta.couplePhoto ||
    customData.couplePhotoUrl ||
    customData.couplePhoto ||
    weddingData.couplePhotoUrl;

  const galleryPhotos: string[] = data.mediaUrls ?? [];
  const [lightboxPhoto, setLightboxPhoto] = useState<string | null>(null);


  // State for Interactive RSVP Modal
  const [isRsvpOpen, setIsRsvpOpen] = useState(false);
  const [guestName, setGuestName] = useState('');
  const [rsvpCount, setRsvpCount] = useState('1');
  const [attending, setAttending] = useState<'yes' | 'no'>('yes');
  const [isSubmitted, setIsSubmitted] = useState(false);

  // Default Traditional Hindu Ritual Events
  const defaultEvents: RitualEvent[] = [
    {
      title: 'Shree Ganesha Sthapana & Haldi',
      devotionalName: 'गणेश पूजन एवं हल्दी उत्सव',
      icon: <Sun className="w-6 h-6 text-[#E5C158]" />,
      date: 'Friday, 11 Dec 2026',
      time: '09:00 AM',
      venue: 'Saraswati Lawn',
      description: 'Blessings of Vignaharta Lord Ganesha followed by vibrant turmeric rituals & dhol music.',
      bgImage: `${ASSET_BASE}/haldi-procession.webp`,
    },
    {
      title: 'Sangeet & Raas Garba',
      devotionalName: 'संगीत एवं रास रंग',
      icon: <Music className="w-6 h-6 text-[#E5C158]" />,
      date: 'Friday, 11 Dec 2026',
      time: '07:00 PM',
      venue: 'Vrindavan Grand Ballroom',
      description: 'An evening of enchanting flute melodies, devotional dance, and celebratory raas garba.',
      bgImage: `${ASSET_BASE}/blue-couple.webp`,
    },
    {
      title: 'Sacred Vivah Phere & Mandap',
      devotionalName: 'शुभ विवाह एवं सप्तपदी',
      icon: <Flame className="w-6 h-6 text-[#E5C158]" />,
      date: 'Saturday, 12 Dec 2026',
      time: '10:30 AM',
      venue: 'Royal Peacock Mandap',
      description: 'The auspicious Vedic wedding ceremony around the sacred fire taking 7 sacred vows.',
      bgImage: `${ASSET_BASE}/royal-couple.webp`,
    },
  ];

  // GSAP ScrollTrigger Animation Setup for Section Backgrounds & Content
  useEffect(() => {
    const ctx = gsap.context(() => {
      // 1. Hero Section Entrance & Background Parallax
      if (heroRef.current) {
        gsap.from(heroRef.current.querySelectorAll('.rk-gsap-hero-item'), {
          opacity: 0,
          y: 40,
          rotateX: -15,
          stagger: 0.15,
          duration: 1.2,
          ease: 'power3.out',
        });
      }

      if (heroBgRef.current) {
        gsap.to(heroBgRef.current, {
          scale: 1.25,
          y: 40,
          ease: 'none',
          scrollTrigger: {
            trigger: heroRef.current,
            start: 'top top',
            end: 'bottom top',
            scrub: true,
          },
        });
      }

      // 1b. Divine Couple Portraits — simple fade/scale reveal on scroll
      if (coupleRef.current) {
        gsap.fromTo(
          coupleRef.current.querySelector('.couple-card'),
          { opacity: 0, y: 30, scale: 0.95 },
          {
            opacity: 1,
            y: 0,
            scale: 1,
            duration: 1,
            scrollTrigger: {
              trigger: coupleRef.current,
              start: 'top 80%',
              toggleActions: 'play reverse play reverse',
            },
          }
        );
      }

      // 2. Ganesha Blessing Section — background shows first, text card reveals after scrolling further in
      if (ganeshaRef.current) {
        gsap.fromTo(
          ganeshaRef.current.querySelector('.ganesha-card'),
          { opacity: 0, scale: 0.85, y: 40 },
          {
            opacity: 1,
            scale: 1,
            y: 0,
            duration: 1,
            scrollTrigger: {
              trigger: ganeshaRef.current,
              start: 'top 55%',
              end: 'top 20%',
              toggleActions: 'play reverse play reverse',
            },
          }
        );
      }

      if (ganeshaBgRef.current) {
        gsap.to(ganeshaBgRef.current, {
          y: -30,
          scale: 1.22,
          ease: 'none',
          scrollTrigger: {
            trigger: ganeshaRef.current,
            start: 'top bottom',
            end: 'bottom top',
            scrub: 1,
          },
        });
      }

      // 3. Vrindavan Serenade Background Parallax
      if (vrindavanRef.current) {
        gsap.to(vrindavanRef.current.querySelectorAll('.rk-particle-floating'), {
          y: -80,
          rotate: 360,
          ease: 'none',
          scrollTrigger: {
            trigger: vrindavanRef.current,
            start: 'top bottom',
            end: 'bottom top',
            scrub: 1.5,
          },
        });
      }

      if (vrindavanBgRef.current) {
        gsap.to(vrindavanBgRef.current, {
          y: -35,
          scale: 1.25,
          ease: 'none',
          scrollTrigger: {
            trigger: vrindavanRef.current,
            start: 'top bottom',
            end: 'bottom top',
            scrub: 1,
          },
        });
      }

      // Vrindavan text card — reveals after the background has been visible for a beat
      if (vrindavanRef.current) {
        gsap.fromTo(
          vrindavanRef.current.querySelector('.vrindavan-card'),
          { opacity: 0, y: 40 },
          {
            opacity: 1,
            y: 0,
            duration: 1,
            scrollTrigger: {
              trigger: vrindavanRef.current,
              start: 'top 55%',
              end: 'top 20%',
              toggleActions: 'play reverse play reverse',
            },
          }
        );
      }

      // 4. Ritual Section — background shows first; header + cards reveal once scrolled further in
      if (ritualContainerRef.current) {
        gsap.fromTo(
          ritualContainerRef.current.querySelector('.ritual-header'),
          { opacity: 0, y: 30 },
          {
            opacity: 1,
            y: 0,
            duration: 0.8,
            scrollTrigger: {
              trigger: ritualContainerRef.current,
              start: 'top 60%',
              end: 'top 30%',
              toggleActions: 'play reverse play reverse',
            },
          }
        );

        const cards = ritualContainerRef.current.querySelectorAll('.rk-ritual-card');
        gsap.set(cards, { opacity: 0, y: 50, rotateX: -10 });
        ScrollTrigger.batch(cards, {
          onEnter: (batch) =>
            gsap.to(batch, { opacity: 1, y: 0, rotateX: 0, stagger: 0.2, overwrite: true }),
          onLeaveBack: (batch) =>
            gsap.to(batch, { opacity: 0, y: 50, rotateX: -10, overwrite: true }),
          start: 'top 55%',
        });
      }

      if (ritualBgRef.current) {
        gsap.to(ritualBgRef.current, {
          y: -35,
          scale: 1.25,
          ease: 'none',
          scrollTrigger: {
            trigger: ritualContainerRef.current,
            start: 'top bottom',
            end: 'bottom top',
            scrub: 1,
          },
        });
      }

      // 5. Venue Section Background Parallax
      if (venueBgRef.current) {
        gsap.to(venueBgRef.current, {
          y: -25,
          scale: 1.2,
          ease: 'none',
          scrollTrigger: {
            trigger: venueRef.current,
            start: 'top bottom',
            end: 'bottom top',
            scrub: 1,
          },
        });
      }

      // Venue text card — reveals after the background has been visible for a beat
      if (venueRef.current) {
        gsap.fromTo(
          venueRef.current.querySelector('.venue-card'),
          { opacity: 0, y: 40 },
          {
            opacity: 1,
            y: 0,
            duration: 1,
            scrollTrigger: {
              trigger: venueRef.current,
              start: 'top 55%',
              end: 'top 20%',
              toggleActions: 'play reverse play reverse',
            },
          }
        );
      }
      // Gallery — batch-reveal photo tiles as they scroll into view
      if (galleryRef.current) {
        const tiles = galleryRef.current.querySelectorAll('.rk-gallery-tile');
        gsap.set(tiles, { opacity: 0, y: 30, scale: 0.92 });
        ScrollTrigger.batch(tiles, {
          onEnter: (batch) =>
            gsap.to(batch, { opacity: 1, y: 0, scale: 1, stagger: 0.08, overwrite: true }),
          onLeaveBack: (batch) =>
            gsap.to(batch, { opacity: 0, y: 30, scale: 0.92, overwrite: true }),
          start: 'top 85%',
        });
      }
    }, containerRef);

    return () => ctx.revert();
  }, []);

  const handleRsvpSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!guestName.trim()) return;
    setIsSubmitted(true);
  };

  return (
    <div ref={containerRef} className="rk-template-root min-h-screen text-[#F7F5EE] bg-[#0D0614]">
      {/* ================= HERO SECTION (Single Scroll Ergonomics & Radha Krishna Artwork Background) ================= */}
      <section
        ref={heroRef}
        className="rk-hero-section relative z-10 w-full min-h-screen flex flex-col justify-between items-center px-4 py-8 text-center overflow-hidden"
      >
        {/* Full-bleed Section Background Image */}
        <div ref={heroBgRef} className="absolute inset-0 z-0 pointer-events-none transform-gpu">
          <img
            src={`${ASSET_BASE}/radha_krishna_hero_bg.jpg`}
            alt="Radha Krishna Hero Backdrop"
            className="w-full h-full object-cover object-center filter brightness-95 contrast-105"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0D0614]/70 via-[#1B0B2E]/65 to-[#0D0614]" />
        </div>

        {/* Header Shloka & Auspicious Banner */}
        <div className="rk-gsap-hero-item relative z-10 w-full pt-4">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#1B0B2E]/90 border border-[#E5C158]/50 backdrop-blur-md mb-3 shadow-xl">
            <Sparkles className="w-4 h-4 text-[#E5C158] animate-pulse" />
            <span className="rk-font-devanagari text-sm text-[#E5C158] tracking-wide">
              ॥ ॐ श्री गणेशाय नमः ॥
            </span>
            <Sparkles className="w-4 h-4 text-[#E5C158] animate-pulse" />
          </div>
          <p className="rk-font-devanagari text-[#FFF2A1]/95 text-sm tracking-widest uppercase font-semibold drop-shadow-md">
            राधे कृष्ण कृपा से पावन विवाह उत्सव
          </p>
        </div>

        {/* Central Couple Titles & Names */}
        <div className="rk-gsap-hero-item relative z-10 my-auto py-6">
          <div className="flex justify-center items-center gap-3 mb-2">
            <span className="h-[1px] w-12 bg-gradient-to-r from-transparent to-[#E5C158]" />
            <span className="rk-font-cinzel text-xs tracking-widest text-[#E5C158] uppercase font-bold">
              Sacred Matrimony
            </span>
            <span className="h-[1px] w-12 bg-gradient-to-l from-transparent to-[#E5C158]" />
          </div>

          <h1 className="rk-font-cinzel text-4xl sm:text-6xl md:text-7xl font-bold rk-gold-text tracking-wide my-2 drop-shadow-lg">
            {groom}
          </h1>

          <div className="flex items-center justify-center gap-3 my-2">
            <Heart className="w-6 h-6 text-[#8A0F3C] fill-[#8A0F3C] animate-pulse" />
            <span className="rk-font-script text-3xl sm:text-4xl text-[#E5C158]">weds</span>
            <Heart className="w-6 h-6 text-[#8A0F3C] fill-[#8A0F3C] animate-pulse" />
          </div>

          <h1 className="rk-font-cinzel text-4xl sm:text-6xl md:text-7xl font-bold rk-gold-text tracking-wide my-2 drop-shadow-lg">
            {bride}
          </h1>

          <p className="max-w-xl mx-auto text-sm sm:text-base text-gray-200 italic px-4 mt-4 font-light leading-relaxed drop-shadow-md">
            "{message}"
          </p>
        </div>

        {/* Above-The-Fold Ceremony Details Bar */}
        <div className="rk-gsap-hero-item relative z-10 w-full max-w-4xl pb-6">
          <div className="rk-glass-card p-4 sm:p-6 w-full shadow-2xl border border-[#E5C158]/40 bg-[#1B0B2E]/80 backdrop-blur-lg">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 items-center text-center sm:text-left">
              <div className="flex items-center justify-center sm:justify-start gap-3">
                <div className="p-2.5 rounded-xl bg-[#8A0F3C]/50 border border-[#E5C158]/50 shadow-inner">
                  <Calendar className="w-5 h-5 text-[#E5C158]" />
                </div>
                <div>
                  <p className="text-xs text-gray-300 font-medium">Date</p>
                  <p className="text-sm font-semibold text-white">{dateStr}</p>
                </div>
              </div>

              <div className="flex items-center justify-center sm:justify-start gap-3">
                <div className="p-2.5 rounded-xl bg-[#8A0F3C]/50 border border-[#E5C158]/50 shadow-inner">
                  <Clock className="w-5 h-5 text-[#E5C158]" />
                </div>
                <div>
                  <p className="text-xs text-gray-300 font-medium">Time</p>
                  <p className="text-sm font-semibold text-white">{timeStr}</p>
                </div>
              </div>

              <div className="flex items-center justify-center sm:justify-start gap-3">
                <div className="p-2.5 rounded-xl bg-[#8A0F3C]/50 border border-[#E5C158]/50 shadow-inner">
                  <MapPin className="w-5 h-5 text-[#E5C158]" />
                </div>
                <div>
                  <p className="text-xs text-gray-300 font-medium">Venue</p>
                  <p className="text-sm font-semibold text-white truncate max-w-[160px]">
                    {venueStr}, {cityStr}
                  </p>
                </div>
              </div>
            </div>

            <div className="mt-5 pt-4 border-t border-white/15 flex flex-wrap gap-3 justify-center items-center">
              <button onClick={() => setIsRsvpOpen(true)} className="rk-btn-primary flex items-center gap-2">
                <Send className="w-4 h-4" />
                <span>RSVP Confirmation</span>
              </button>

              <a
                href={mapLink}
                target="_blank"
                rel="noopener noreferrer"
                className="px-5 py-3 rounded-full bg-white/15 hover:bg-white/25 text-white text-xs font-semibold flex items-center gap-2 border border-white/30 transition-all"
              >
                <Navigation className="w-4 h-4 text-[#E5C158]" />
                <span>Get Directions</span>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* ================= DIVINE COUPLE PORTRAITS (Real Uploaded Photos, When Provided) ================= */}
      {(groomPhoto || bridePhoto) && (
        <section ref={coupleRef} className="relative z-10 py-16 px-4 w-full flex items-center justify-center">
          <div className="couple-card rk-glass-card p-8 sm:p-10 border border-[#E5C158]/50 shadow-2xl bg-[#1B0B2E]/85 backdrop-blur-lg max-w-3xl mx-auto w-full flex flex-col sm:flex-row items-center justify-center gap-6 sm:gap-10">
            <div className="flex flex-col items-center text-center">
              {bridePhoto ? (
                <div className="w-28 h-28 sm:w-36 sm:h-36 rounded-full border-4 border-[#E5C158] shadow-lg overflow-hidden mb-3 bg-[#0D0614]">
                  <img src={bridePhoto} alt={`${bride}, the bride`} className="w-full h-full object-cover" />
                </div>
              ) : (
                <div className="w-28 h-28 sm:w-36 sm:h-36 rounded-full border-4 border-[#E5C158]/40 shadow-lg mb-3 bg-[#0D0614] flex items-center justify-center">
                  <User className="w-10 h-10 text-[#E5C158]/40" />
                </div>
              )}
              <h3 className="rk-font-cinzel text-lg sm:text-xl font-bold rk-gold-text">{bride}</h3>
              <span className="rk-font-devanagari text-xs text-[#E5C158]/80 uppercase tracking-widest mt-1">Bride</span>
            </div>

            <Heart className="w-8 h-8 text-[#8A0F3C] fill-[#8A0F3C] shrink-0" />

            <div className="flex flex-col items-center text-center">
              {groomPhoto ? (
                <div className="w-28 h-28 sm:w-36 sm:h-36 rounded-full border-4 border-[#E5C158] shadow-lg overflow-hidden mb-3 bg-[#0D0614]">
                  <img src={groomPhoto} alt={`${groom}, the groom`} className="w-full h-full object-cover" />
                </div>
              ) : (
                <div className="w-28 h-28 sm:w-36 sm:h-36 rounded-full border-4 border-[#E5C158]/40 shadow-lg mb-3 bg-[#0D0614] flex items-center justify-center">
                  <User className="w-10 h-10 text-[#E5C158]/40" />
                </div>
              )}
              <h3 className="rk-font-cinzel text-lg sm:text-xl font-bold rk-gold-text">{groom}</h3>
              <span className="rk-font-devanagari text-xs text-[#E5C158]/80 uppercase tracking-widest mt-1">Groom</span>
            </div>
          </div>
        </section>
      )}

      {/* ================= SCENE 1: SHREE GANESHA BLESSING (FULL SECTION GANESHA BACKGROUND) ================= */}
      <section ref={ganeshaRef} className="relative z-10 py-24 px-4 w-full min-h-[75vh] flex items-center justify-center overflow-hidden rk-3d-viewport">
        {/* Full-bleed Section Background Image */}
        <div ref={ganeshaBgRef} className="absolute inset-0 z-0 pointer-events-none transform-gpu">
          <img
            src={`${ASSET_BASE}/ganesha_blessing_bg.jpg`}
            alt="Ganesha Divine Section Background"
            className="w-full h-full object-cover object-center filter brightness-100 contrast-110"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0D0614]/45 via-[#0D0614]/10 to-[#0D0614]/60" />
        </div>

        <div className="ganesha-card rk-glass-card rk-3d-card p-8 sm:p-10 text-center border border-[#E5C158]/50 shadow-2xl relative z-10 max-w-4xl mx-auto bg-[#1B0B2E]/85 backdrop-blur-lg">
          <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-gradient-to-b from-[#E5C158] to-[#B88E28] p-0.5 flex items-center justify-center shadow-lg relative z-10">
            <div className="w-full h-full bg-[#0D0614] rounded-full flex items-center justify-center">
              <span className="rk-font-devanagari text-3xl rk-gold-text font-bold">ॐ</span>
            </div>
          </div>

          <h2 className="rk-font-devanagari text-2xl sm:text-3xl font-bold text-[#E5C158] mb-2 relative z-10">
            वक्रतुण्ड महाकाय सूर्यकोटि समप्रभ। निर्विघ्नं कुरु मे देव सर्वकार्येषु सर्वदा॥
          </h2>

          <p className="text-gray-200 text-sm sm:text-base max-w-2xl mx-auto leading-relaxed mt-4 relative z-10">
            May Lord Ganesha remove all obstacles and shower his divine grace on {groom} & {bride} as
            they embark on this sacred lifelong journey together.
          </p>

          <p className="mt-6 text-xs tracking-widest text-[#E5C158] font-semibold uppercase relative z-10">
            Blessings Cordially Invited By: {host}
          </p>
        </div>
      </section>

      {/* ================= SCENE 2: RADHA KRISHNA VRINDAVAN SERENADE (FULL SECTION VRINDAVAN BACKGROUND) ================= */}
      <section ref={vrindavanRef} className="relative z-10 py-28 px-4 w-full min-h-[80vh] flex items-center justify-center text-center overflow-hidden">
        {/* Full-bleed Section Background Image */}
        <div ref={vrindavanBgRef} className="absolute inset-0 pointer-events-none z-0 transform-gpu">
          <img
            src={`${ASSET_BASE}/vrindavan_raas_bg.jpg`}
            alt="Vrindavan Raas Section Background"
            className="w-full h-full object-cover object-center filter brightness-100 contrast-115"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0D0614]/45 via-[#0D0614]/10 to-[#0D0614]/60" />
        </div>

        {/* Floating Parallax Accents */}
        <div className="rk-particle-floating absolute top-10 left-10 text-3xl opacity-90 pointer-events-none z-10">
          🪶
        </div>
        <div className="rk-particle-floating absolute bottom-10 right-10 text-4xl opacity-90 pointer-events-none z-10">
          🪷
        </div>
        <div className="rk-particle-floating absolute top-1/2 left-6 text-2xl opacity-70 pointer-events-none z-10">
          🎶
        </div>

        <div className="vrindavan-card rk-glass-card p-10 sm:p-12 relative z-10 border border-[#8A0F3C]/60 bg-[#1B0B2E]/85 backdrop-blur-lg max-w-4xl mx-auto shadow-2xl">
          <div className="inline-block px-4 py-1.5 rounded-full bg-[#8A0F3C]/50 border border-[#E5C158]/40 text-[#E5C158] text-xs font-semibold uppercase tracking-widest mb-4">
            Divine Serenade
          </div>

          <h2 className="rk-font-cinzel text-3xl sm:text-5xl font-bold rk-gold-text mb-4">
            Vrindavan Raas & Eternal Love
          </h2>

          <p className="text-gray-200 text-sm sm:text-base max-w-3xl mx-auto leading-relaxed font-light">
            Like Radha’s devotion and Krishna’s eternal flute melody, true love brings harmony to the universe.
            Join us under the divine canopy of Vrindavan as we celebrate this holy union.
          </p>
        </div>
      </section>

      {/* ================= SCENE 3: SACRED RITUAL CEREMONIES (FULL SECTION MANDAP BACKGROUND) ================= */}
      <section ref={ritualContainerRef} className="relative z-10 py-24 px-4 w-full min-h-[85vh] flex flex-col justify-center items-center overflow-hidden">
        {/* Full-bleed Section Background Image */}
        <div ref={ritualBgRef} className="absolute inset-0 pointer-events-none z-0 transform-gpu">
          <img
            src={`${ASSET_BASE}/sacred_rituals_bg.jpg`}
            alt="Sacred Rituals Section Background"
            className="w-full h-full object-cover object-center filter brightness-100 contrast-115"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0D0614]/45 via-[#0D0614]/10 to-[#0D0614]/60" />
        </div>

        <div className="ritual-header relative z-10 w-full max-w-5xl mx-auto text-center mb-12">
          <span className="rk-font-devanagari text-[#E5C158] text-sm tracking-widest uppercase font-semibold">
            मांगल्य उत्सव
          </span>
          <h2 className="rk-font-cinzel text-3xl sm:text-5xl font-bold rk-gold-text mt-1">
            Ceremonial Schedule
          </h2>
          <p className="text-gray-300 text-xs sm:text-sm mt-2">
            Detailed timeline of auspicious rituals and joyful celebrations
          </p>
        </div>

        <div className="relative z-10 w-full max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-6">
          {defaultEvents.map((event, index) => (
            <div
              key={index}
              className="rk-ritual-card rk-glass-card p-6 border border-[#E5C158]/40 flex flex-col justify-between hover:border-[#E5C158] transition-all duration-300 transform relative overflow-hidden bg-[#1B0B2E]/90 backdrop-blur-md shadow-2xl group"
            >
              <div className="relative z-10">
                <div className="flex items-center justify-between mb-4">
                  <div className="p-3 rounded-2xl bg-[#8A0F3C]/50 border border-[#E5C158]/40">
                    {event.icon}
                  </div>
                  <span className="rk-font-devanagari text-xs text-[#E5C158] bg-white/10 px-2.5 py-1 rounded-full border border-white/20">
                    {event.devotionalName}
                  </span>
                </div>

                <h3 className="rk-font-cinzel text-lg font-bold text-white mb-2">{event.title}</h3>
                <p className="text-xs text-gray-200 mb-4 leading-relaxed">{event.description}</p>
              </div>

              <div className="relative z-10 pt-4 border-t border-white/15 text-xs space-y-2">
                <div className="flex items-center gap-2 text-gray-200">
                  <Calendar className="w-4 h-4 text-[#E5C158]" />
                  <span>{event.date}</span>
                </div>
                <div className="flex items-center gap-2 text-gray-200">
                  <Clock className="w-4 h-4 text-[#E5C158]" />
                  <span>{event.time}</span>
                </div>
                <div className="flex items-center gap-2 text-gray-200">
                  <MapPin className="w-4 h-4 text-[#E5C158]" />
                  <span className="truncate">{event.venue}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ================= PHOTO GALLERY (Real Uploaded Photos, When Provided) ================= */}
      {galleryPhotos.length > 0 && (
        <section ref={galleryRef} className="relative z-10 py-16 px-4 w-full max-w-5xl mx-auto">
          <div className="text-center mb-10">
            <span className="rk-font-devanagari text-[#E5C158] text-sm tracking-widest uppercase font-semibold inline-flex items-center gap-2 justify-center">
              <Images className="w-4 h-4" /> मंगल स्मृतियाँ
            </span>
            <h2 className="rk-font-cinzel text-3xl sm:text-4xl font-bold rk-gold-text mt-1">Our Moments</h2>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 sm:gap-4">
            {galleryPhotos.map((url, idx) => (
              <button
                key={idx}
                type="button"
                onClick={() => setLightboxPhoto(url)}
                className="rk-gallery-tile rounded-2xl overflow-hidden border border-[#E5C158]/30 shadow-lg aspect-square group cursor-zoom-in"
              >
                <img
                  src={url}
                  alt={`Wedding memory ${idx + 1}`}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
              </button>
            ))}
          </div>
        </section>
      )}

      {/* ================= SCENE 4: VENUE MAP & FOOTER (FULL SECTION PALACE BACKDROP) ================= */}
      <section ref={venueRef} className="relative z-10 py-24 px-4 w-full min-h-[70vh] flex flex-col justify-center items-center text-center overflow-hidden">
        {/* Full-bleed Section Background Image */}
        <div ref={venueBgRef} className="absolute inset-0 pointer-events-none z-0 transform-gpu">
          <img
            src={`${ASSET_BASE}/mandap-forest.webp`}
            alt="Venue Section Background"
            className="w-full h-full object-cover object-center filter brightness-100 contrast-110"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0D0614]/45 via-[#0D0614]/10 to-[#0D0614]/60" />
        </div>

        <div className="venue-card rk-glass-card p-8 sm:p-10 border border-[#E5C158]/50 shadow-2xl relative z-10 max-w-4xl mx-auto w-full bg-[#1B0B2E]/90 backdrop-blur-lg">
          <MapPin className="w-10 h-10 text-[#E5C158] mx-auto mb-3" />
          <h2 className="rk-font-cinzel text-2xl sm:text-3xl font-bold text-white mb-2">
            Wedding Venue Location
          </h2>
          <p className="text-lg font-semibold text-[#E5C158]">{venueStr}</p>
          <p className="text-sm text-gray-200 max-w-md mx-auto mt-1 mb-6">{addressStr}</p>

          <div className="flex flex-wrap justify-center gap-4">
            <a
              href={mapLink}
              target="_blank"
              rel="noopener noreferrer"
              className="rk-btn-primary inline-flex items-center gap-2"
            >
              <Navigation className="w-4 h-4" />
              <span>Open in Google Maps</span>
            </a>

            <button
              onClick={() => setIsRsvpOpen(true)}
              className="px-6 py-3 rounded-full bg-white/15 hover:bg-white/25 text-white font-semibold text-sm border border-white/30 transition-all flex items-center gap-2"
            >
              <Send className="w-4 h-4 text-[#E5C158]" />
              <span>Confirm RSVP</span>
            </button>
          </div>
        </div>

        <div className="mt-12 text-center text-xs text-gray-400 space-y-1 relative z-10">
          <p className="rk-font-devanagari text-gray-300">॥ सर्वे भवन्तु सुखिनः सर्वे सन्तु निरामयाः ॥</p>
          <p>© {new Date().getFullYear()} {groom} & {bride} Wedding Invitation. All blessings reserved.</p>
        </div>
      </section>

      {/* ================= INTERACTIVE RSVP MODAL ================= */}
      {isRsvpOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/85 backdrop-blur-md">
          <div className="rk-glass-card w-full max-w-md p-6 relative border border-[#E5C158]/60 shadow-2xl bg-[#1B0B2E] animate-in fade-in zoom-in-95 duration-200">
            <button
              onClick={() => setIsRsvpOpen(false)}
              className="absolute top-4 right-4 text-gray-400 hover:text-white p-1 rounded-full bg-white/10"
            >
              <X className="w-5 h-5" />
            </button>

            {!isSubmitted ? (
              <form onSubmit={handleRsvpSubmit} className="space-y-4">
                <div className="text-center mb-4">
                  <span className="rk-font-devanagari text-xs text-[#E5C158] uppercase tracking-widest font-semibold">
                    शुभ उपस्थिति
                  </span>
                  <h3 className="rk-font-cinzel text-xl font-bold text-white">RSVP Confirmation</h3>
                  <p className="text-xs text-gray-300 mt-1">Please confirm your attendance for the wedding</p>
                </div>

                <div>
                  <label className="block text-xs text-gray-300 mb-1 font-medium">Your Full Name</label>
                  <input
                    type="text"
                    required
                    value={guestName}
                    onChange={(e) => setGuestName(e.target.value)}
                    placeholder="e.g. Rajesh Sharma & Family"
                    className="w-full px-4 py-2.5 rounded-xl bg-white/10 border border-white/20 text-white placeholder-gray-500 focus:outline-none focus:border-[#E5C158] text-sm"
                  />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs text-gray-300 mb-1 font-medium">Attendance</label>
                    <select
                      value={attending}
                      onChange={(e) => setAttending(e.target.value as 'yes' | 'no')}
                      className="w-full px-3 py-2.5 rounded-xl bg-[#1B0B2E] border border-white/20 text-white text-sm focus:outline-none focus:border-[#E5C158]"
                    >
                      <option value="yes">Joyfully Accept</option>
                      <option value="no">Regretfully Decline</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-xs text-gray-300 mb-1 font-medium">Guest Count</label>
                    <input
                      type="number"
                      min="1"
                      max="10"
                      value={rsvpCount}
                      onChange={(e) => setRsvpCount(e.target.value)}
                      className="w-full px-4 py-2.5 rounded-xl bg-white/10 border border-white/20 text-white text-sm focus:outline-none focus:border-[#E5C158]"
                    />
                  </div>
                </div>

                <button type="submit" className="w-full mt-2 rk-btn-primary flex items-center justify-center gap-2">
                  <Send className="w-4 h-4" />
                  <span>Submit RSVP</span>
                </button>
              </form>
            ) : (
              <div className="text-center py-6 space-y-3">
                <CheckCircle2 className="w-12 h-12 text-[#E5C158] mx-auto animate-bounce" />
                <h3 className="rk-font-cinzel text-xl font-bold text-white">RSVP Received!</h3>
                <p className="text-xs text-gray-300 max-w-xs mx-auto">
                  Thank you <span className="text-[#E5C158] font-semibold">{guestName}</span>! Your RSVP response has been recorded with blessings.
                </p>
                <button
                  onClick={() => {
                    setIsSubmitted(false);
                    setIsRsvpOpen(false);
                  }}
                  className="mt-4 px-6 py-2 rounded-full bg-white/10 hover:bg-white/20 text-white text-xs font-semibold border border-white/20"
                >
                  Close
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ================= GALLERY LIGHTBOX ================= */}
      {lightboxPhoto && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/90 backdrop-blur-md cursor-zoom-out"
          onClick={() => setLightboxPhoto(null)}
        >
          <button
            onClick={() => setLightboxPhoto(null)}
            className="absolute top-4 right-4 text-gray-300 hover:text-white p-2 rounded-full bg-white/10 z-10"
            aria-label="Close enlarged photo"
          >
            <X className="w-6 h-6" />
          </button>
          <img
            src={lightboxPhoto}
            alt="Enlarged wedding memory"
            className="max-w-full max-h-full rounded-2xl border border-[#E5C158]/40 shadow-2xl object-contain"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
}

// Auto-register in the template registry
registry.register({
  metadata: META,
  component: GSAPRadhaKrishnaTemplate,
});
